import React, { useMemo, useState } from 'react'
import {
  AcademicCapIcon,
  ArrowPathIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  FaceSmileIcon,
  SparklesIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import {
  useGetEducationAnalyticsQuery,
  EducationMetrics,
  EducationOrgBreakdown,
  EducationProjectBreakdown,
  EducationFeedbackRow,
} from '../store/api/educationApi'

const TODAY = new Date().toISOString().slice(0, 10)

function formatPercent(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—'
  return `${Math.round(value)}%`
}

function formatScore(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—'
  return value.toFixed(1)
}

function formatQuality(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—'
  // quality_improvement is 0–1; show as percent
  return `${Math.round(value * 100)}%`
}

function formatTimeSaved(seconds: number | null | undefined): string {
  if (seconds == null || Number.isNaN(seconds) || seconds <= 0) return '—'
  const t = Math.floor(seconds)
  const h = Math.floor(t / 3600)
  const m = Math.floor((t % 3600) / 60)
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m`
  return `${t}s`
}

function formatNumber(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—'
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 })
}

interface KpiCardProps {
  label: string
  value: string
  sub?: string
  icon: React.ElementType
  color: string
}

function KpiCard({ label, value, sub, icon: Icon, color }: KpiCardProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 flex items-start gap-4">
      <div className={`flex-shrink-0 w-11 h-11 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-0.5 leading-none">{value}</p>
        {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
      </div>
    </div>
  )
}

function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} />
}

function metricsCells(m: EducationMetrics) {
  return (
    <>
      <td className="py-2 px-3 text-right tabular-nums text-gray-800">{formatNumber(m.studentsUsedAi)}</td>
      <td className="py-2 px-3 text-right tabular-nums text-gray-800">{formatNumber(m.avgPromptsPerStudent)}</td>
      <td className="py-2 px-3 text-right tabular-nums text-gray-800">{formatPercent(m.pctFollowUp)}</td>
      <td className="py-2 px-3 text-right tabular-nums text-gray-800">{formatPercent(m.pctSources)}</td>
      <td className="py-2 px-3 text-right tabular-nums text-gray-800">{formatPercent(m.pctRevised)}</td>
      <td className="py-2 px-3 text-right tabular-nums text-gray-800">{formatNumber(m.copyPasteRiskFlags)}</td>
      <td className="py-2 px-3 text-right tabular-nums text-gray-800">{formatTimeSaved(m.teacherTimeSavedSec)}</td>
      <td className="py-2 px-3 text-right tabular-nums text-gray-800">{formatScore(m.teacherSatisfactionAvg)}</td>
      <td className="py-2 px-3 text-right tabular-nums text-gray-800">{formatScore(m.studentSatisfactionAvg)}</td>
      <td className="py-2 px-3 text-right tabular-nums text-gray-800">{formatQuality(m.qualityImprovementAvg)}</td>
    </>
  )
}

const METRIC_HEADERS = [
  'Students',
  'Avg prompts',
  'Follow-up %',
  'Sources %',
  'Revised %',
  'Paste flags',
  'Time saved',
  'Teacher sat.',
  'Student sat.',
  'Quality ↑',
]

function BreakdownTable({
  title,
  subtitle,
  nameHeader,
  rows,
  loading,
}: {
  title: string
  subtitle: string
  nameHeader: string
  rows: Array<{ key: string; name: string; sub?: string; metrics: EducationMetrics }>
  loading: boolean
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
      </div>
      {loading ? (
        <Skeleton className="h-48" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-8">No data for this filter.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[960px]">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left pb-2 pr-3 text-xs text-gray-500 font-medium uppercase">{nameHeader}</th>
                {METRIC_HEADERS.map((h) => (
                  <th key={h} className="text-right pb-2 px-3 text-xs text-gray-500 font-medium uppercase whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((r) => (
                <tr key={r.key} className="hover:bg-gray-50">
                  <td className="py-2 pr-3">
                    <p className="font-medium text-gray-800">{r.name}</p>
                    {r.sub && <p className="text-xs text-gray-400">{r.sub}</p>}
                  </td>
                  {metricsCells(r.metrics)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const Education: React.FC = () => {
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState(TODAY)
  const [organizationId, setOrganizationId] = useState('')
  const [projectId, setProjectId] = useState('')

  const [appliedFrom, setAppliedFrom] = useState('')
  const [appliedTo, setAppliedTo] = useState('')
  const [appliedOrg, setAppliedOrg] = useState('')
  const [appliedProject, setAppliedProject] = useState('')

  const queryParams = useMemo(
    () => ({
      dateFrom: appliedFrom || undefined,
      dateTo: appliedTo || undefined,
      organizationId: appliedOrg || undefined,
      projectId: appliedProject || undefined,
    }),
    [appliedFrom, appliedTo, appliedOrg, appliedProject],
  )

  const { data: resp, isLoading, isFetching, isError, refetch } = useGetEducationAnalyticsQuery(queryParams)
  const d = resp?.data
  const loading = isLoading || isFetching

  const filteredProjects = useMemo(() => {
    const all = d?.projects ?? []
    if (!organizationId) return all
    const oid = Number(organizationId)
    return all.filter((p) => p.organizationId === oid)
  }, [d?.projects, organizationId])

  function applyFilter() {
    setAppliedFrom(dateFrom)
    setAppliedTo(dateTo)
    setAppliedOrg(organizationId)
    setAppliedProject(projectId)
  }

  function clearFilter() {
    setDateFrom('')
    setDateTo(TODAY)
    setOrganizationId('')
    setProjectId('')
    setAppliedFrom('')
    setAppliedTo('')
    setAppliedOrg('')
    setAppliedProject('')
  }

  const orgRows = useMemo(
    () =>
      (d?.byOrganization ?? []).map((r: EducationOrgBreakdown) => ({
        key: String(r.organizationId),
        name: r.organizationName,
        metrics: r,
      })),
    [d?.byOrganization],
  )

  const projectRows = useMemo(
    () =>
      (d?.byProject ?? []).map((r: EducationProjectBreakdown) => ({
        key: String(r.projectId),
        name: r.projectName,
        sub: r.organizationName,
        metrics: r,
      })),
    [d?.byProject],
  )

  const s = d?.summary

  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Education Analytics</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Motionode Education AI workspace usage, integrity signals, and satisfaction
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <CalendarIcon className="w-4 h-4 text-gray-400 hidden sm:block" />
          <input
            type="date"
            value={dateFrom}
            max={TODAY}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
          />
          <span className="text-gray-400 text-sm">→</span>
          <input
            type="date"
            value={dateTo}
            max={TODAY}
            onChange={(e) => setDateTo(e.target.value)}
            className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
          />
          <select
            value={organizationId}
            onChange={(e) => {
              setOrganizationId(e.target.value)
              setProjectId('')
            }}
            className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 max-w-[180px]"
          >
            <option value="">All organizations</option>
            {(d?.organizations ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 max-w-[180px]"
          >
            <option value="">All projects</option>
            {filteredProjects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <button
            onClick={applyFilter}
            className="px-3.5 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Apply
          </button>
          {(appliedFrom || appliedTo || appliedOrg || appliedProject) && (
            <button
              onClick={clearFilter}
              className="px-3 py-1.5 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50 transition-colors"
            >
              Clear
            </button>
          )}
          <button
            onClick={() => refetch()}
            disabled={loading}
            title="Refresh"
            className="p-1.5 border border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-40 transition-colors"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {d?.note && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-amber-800 text-sm">
          {d.note}
        </div>
      )}

      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
          Failed to load education analytics. Check the server connection and try refreshing.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {loading && !s ? (
          Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} className="h-24" />)
        ) : s ? (
          <>
            <KpiCard
              label="Students (AI)"
              value={formatNumber(s.studentsUsedAi)}
              sub="Used AI workspace"
              icon={UserGroupIcon}
              color="bg-indigo-500"
            />
            <KpiCard
              label="Avg prompts / student"
              value={formatNumber(s.avgPromptsPerStudent)}
              icon={ChatBubbleLeftRightIcon}
              color="bg-blue-500"
            />
            <KpiCard
              label="Follow-up questions"
              value={formatPercent(s.pctFollowUp)}
              sub="% of students"
              icon={SparklesIcon}
              color="bg-violet-500"
            />
            <KpiCard
              label="Requested sources"
              value={formatPercent(s.pctSources)}
              sub="% of students"
              icon={AcademicCapIcon}
              color="bg-emerald-500"
            />
            <KpiCard
              label="Revised answer"
              value={formatPercent(s.pctRevised)}
              sub="% of students"
              icon={DocumentDuplicateIcon}
              color="bg-teal-500"
            />
            <KpiCard
              label="Copy/paste flags"
              value={formatNumber(s.copyPasteRiskFlags)}
              icon={DocumentDuplicateIcon}
              color="bg-rose-500"
            />
            <KpiCard
              label="Teacher time saved"
              value={formatTimeSaved(s.teacherTimeSavedSec)}
              icon={ClockIcon}
              color="bg-amber-500"
            />
            <KpiCard
              label="Teacher satisfaction"
              value={formatScore(s.teacherSatisfactionAvg)}
              sub="Avg 1–5"
              icon={FaceSmileIcon}
              color="bg-orange-500"
            />
            <KpiCard
              label="Student satisfaction"
              value={formatScore(s.studentSatisfactionAvg)}
              sub="Avg 1–5"
              icon={FaceSmileIcon}
              color="bg-sky-500"
            />
            <KpiCard
              label="Quality improvement"
              value={formatQuality(s.qualityImprovementAvg)}
              sub="First draft → final"
              icon={SparklesIcon}
              color="bg-fuchsia-500"
            />
          </>
        ) : null}
      </div>

      <BreakdownTable
        title="By organization"
        subtitle="Education institutions only (industry = education)"
        nameHeader="Organization"
        rows={orgRows}
        loading={loading && orgRows.length === 0}
      />

      <BreakdownTable
        title="By project"
        subtitle="Classes / projects within education organizations"
        nameHeader="Project"
        rows={projectRows}
        loading={loading && projectRows.length === 0}
      />

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">Recent teacher & student evaluations</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Scores and optional comments from unit-completed and review prompts
          </p>
        </div>
        {loading && !(d?.recentFeedback || []).length ? (
          <div className="p-5 space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : (d?.recentFeedback || []).length === 0 ? (
          <p className="px-5 py-6 text-sm text-gray-500">No submitted evaluations in this date range.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="py-2 px-4 font-medium">When</th>
                  <th className="py-2 px-4 font-medium">Who</th>
                  <th className="py-2 px-4 font-medium">Role</th>
                  <th className="py-2 px-4 font-medium">Kind</th>
                  <th className="py-2 px-4 font-medium text-right">Score</th>
                  <th className="py-2 px-4 font-medium">Comment</th>
                  <th className="py-2 px-4 font-medium">Context</th>
                </tr>
              </thead>
              <tbody>
                {(d?.recentFeedback as EducationFeedbackRow[] | undefined || []).map((row) => (
                  <tr key={row.id} className="border-t border-gray-100 align-top">
                    <td className="py-2.5 px-4 text-gray-600 whitespace-nowrap">
                      {row.submittedAt ? new Date(row.submittedAt).toLocaleString() : '—'}
                    </td>
                    <td className="py-2.5 px-4">
                      <div className="font-medium text-gray-900">{row.userName || '—'}</div>
                      <div className="text-xs text-gray-500">{row.userEmail || ''}</div>
                    </td>
                    <td className="py-2.5 px-4 capitalize text-gray-700">{row.actorRole || '—'}</td>
                    <td className="py-2.5 px-4 text-gray-700">{row.promptKind || '—'}</td>
                    <td className="py-2.5 px-4 text-right tabular-nums font-semibold text-gray-900">
                      {row.score != null ? row.score : '—'}
                    </td>
                    <td className="py-2.5 px-4 text-gray-700 max-w-xs">
                      {row.comment ? <span className="whitespace-pre-wrap">{row.comment}</span> : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="py-2.5 px-4 text-gray-600">
                      <div>{row.organizationName || '—'}</div>
                      <div className="text-xs text-gray-500">{row.projectName || ''}</div>
                      {row.deliverableTitle ? (
                        <div className="text-xs text-indigo-600 mt-0.5">{row.deliverableTitle}</div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default Education
